import type { CampaignAttribution } from "@lingoshade/domain";

const retentionMs = 30 * 60 * 1000;
const names = {
  source: "utm_source",
  medium: "utm_medium",
  campaign: "utm_campaign",
  content: "utm_content",
  term: "utm_term",
  referrerHost: "_ls_ref",
} as const;
interface SavedAttribution {
  attribution: CampaignAttribution;
  expiresAt: number;
}
let memory: SavedAttribution | undefined;

function externalHost(host: string): boolean {
  const current = window.location.hostname;
  const landingHost = current.replace(/^app\./, "");
  return (
    !new Set([
      current,
      landingHost,
      `www.${landingHost}`,
      "lingoshade.com",
      "www.lingoshade.com",
      "app.lingoshade.com",
      "localhost",
      "127.0.0.1",
    ]).has(host) &&
    /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/.test(host)
  );
}

function validValue(
  field: keyof CampaignAttribution,
  value: unknown,
): value is string {
  return (
    typeof value === "string" &&
    value.length > 0 &&
    value.length <= (field === "referrerHost" ? 253 : 120) &&
    (field !== "referrerHost" || externalHost(value.toLowerCase())) &&
    !Array.from(value).some(
      (character) =>
        character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127,
    )
  );
}

function readSaved(): CampaignAttribution | undefined {
  if (memory && memory.expiresAt <= Date.now()) memory = undefined;
  return memory?.attribution;
}

function remember(attribution: CampaignAttribution): void {
  // Re-reading the same URL must not extend the in-memory retention window.
  if (JSON.stringify(readSaved()) === JSON.stringify(attribution)) return;
  memory = { attribution, expiresAt: Date.now() + retentionMs };
}

/** Last tagged touch lives only in this document, for up to 30 minutes. */
export function captureCampaignAttribution(search: string): void {
  const params = new URLSearchParams(search);
  const attribution: CampaignAttribution = {};
  for (const [field, parameter] of Object.entries(names) as Array<
    [keyof CampaignAttribution, string]
  >) {
    const value = params.get(parameter)?.trim();
    if (validValue(field, value))
      attribution[field] =
        field === "referrerHost" ? value.toLowerCase() : value;
  }
  if (Object.values(attribution).some(Boolean) && !attribution.referrerHost) {
    try {
      const host = new URL(document.referrer).hostname;
      if (externalHost(host)) attribution.referrerHost = host;
    } catch {
      // Only an available external hostname can supplement the query fields.
    }
  }
  if (!Object.values(attribution).some(Boolean)) return;
  const tagged =
    attribution.source ||
    attribution.medium ||
    attribution.campaign ||
    attribution.content ||
    attribution.term;
  const saved = readSaved();
  if (
    !tagged &&
    (saved?.source ||
      saved?.medium ||
      saved?.campaign ||
      saved?.content ||
      saved?.term)
  )
    return;
  remember(attribution);
}

export function getCampaignAttribution(): CampaignAttribution | undefined {
  if (typeof window === "undefined") return undefined;
  captureCampaignAttribution(window.location.search);
  const saved = readSaved();
  if (saved) return saved;
  try {
    const host = new URL(document.referrer).hostname;
    if (!externalHost(host)) return undefined;
    const attribution = { referrerHost: host };
    remember(attribution);
    return attribution;
  } catch {
    return undefined;
  }
}

export function clearCampaignAttribution(): void {
  memory = undefined;
  try {
    localStorage.removeItem("lingoshade_campaign_attribution");
  } catch {
    /* Storage can be disabled. */
  }
}
